/* eslint-disable max-lines, max-len */
export default `# Default form template derived from current enabled form drafts
# (most common sections and question identities in dev DB).
[[layout]]
name = "apply"

[[layout.group]]
name = "submitter"
title = "Submitter"
description = "Submitter info"
questions = [
  ["are_you_applicant", "submitter"],
]

[[layout.group]]
name = "applicant"
title = "Applicant Info"
description = "Enter the applicant's core contact and identity information."
questions = [
  ["name_first", "applicant"],
  ["name_last", "applicant"],
  ["email", "applicant"],
  ["phone", "applicant"],
  ["allows_transactional_text_messages", "applicant"],
  ["phone_alternate", "applicant"],
  ["date_of_birth", "applicant"],
  ["gender", "applicant"],
]

[[layout.group]]
name = "members"
title = "Household Members"
description = "Share the current household member status."
questions = [
  ["household_members_status", "household"],
]

[[layout.group]]
name = "address"
title = "Address Information"
description = "Provide current residence and mailing details."
questions = [
  ["address_residence", "applicant"],
  ["mailing_address_type", "applicant"],
]

[[layout.group]]
name = "income"
title = "Income"
description = "Provide total household income and frequency."
questions = [
  ["household_income", "household"],
  ["income_frequency", "household"],
]

[[layout.group]]
name = "referral"
title = "Referral"
description = "Tell us how the applicant heard about this property."
questions = [
  ["marketing_how_did_applicant_hear_about_property", "applicant"],
]

#-----Group: OPTIONAL CONTACT -----
[[layout.group]]
name = "contact"
title = "Optional Contact"
description = "Add someone we can contact if we cannot reach you."
questions = [
  ["applicant_relationship", "contact"],
]

[[layout.group]]
name = "additional"
title = "Additional Information"
description = "Answer additional household preference and accessibility questions."
questions = [
  ["unit_size", "applicant"],
  ["has_section_8_voucher", "household"],
  ["household_requires_impairment_mobility", "household"],
  ["household_requires_impairment_visual", "household"],
  ["household_requires_impairment_hearing", "household"],
  ["household_receives_other_housing_assistance", "household"],
  ["student_household", "household"],
]

#-----Group: CERTIFICATION -----
[[layout.group]]
name = "review-and-sign"
title = "Review and Sign"
description = "Review terms and capture consent."
questions = [
  ["formatted_agree_to_terms", "applicant"],
  ["agree_to_terms", "applicant"],
  ["allows_marketing_texts", "applicant"],
  ["formatted_footer_text", "applicant"],
]

[[question]]
name = ["are_you_applicant", "submitter"]
title = "Are you the applicant?"
required = true
children = [
  ["name_first", "submitter"],
  ["name_last", "submitter"],
  ["email", "submitter"],
  ["phone", "submitter"],
  ["applicant_relationship", "submitter"],
  ["contact_for_updates", "submitter"],
]

[[question]]
name = ["name_first", "submitter"]
title = "Submitter first name"
required = true
cond = { type = "includes", values = ["false"] }

[[question]]
name = ["name_last", "submitter"]
title = "Submitter last name"
required = true
cond = { type = "includes", values = ["false"] }

[[question]]
name = ["email", "submitter"]
title = "Submitter email"
required = true
cond = { type = "includes", values = ["false"] }

[[question]]
name = ["phone", "submitter"]
title = "Submitter phone number"
required = true
cond = { type = "includes", values = ["false"] }

[[question]]
name = ["applicant_relationship", "submitter"]
title = "Relationship to applicant"
required = true
cond = { type = "includes", values = ["false"] }
data = { options = [
  { label = "Family member", value = "family_member" },
  { label = "Friend", value = "friend" },
  { label = "Case manager", value = "case_manager" },
  { label = "Other", value = "other" },
] }

[[question]]
name = ["contact_for_updates", "submitter"]
title = "Can we contact you for follow-up updates?"
required = false
cond = { type = "includes", values = ["false"] }

[[question]]
name = ["name_first", "applicant"]
title = "First name"
required = true

[[question]]
name = ["name_last", "applicant"]
title = "Last name"
required = true

[[question]]
name = ["email", "applicant"]
title = "Email"
required = true

[[question]]
name = ["phone", "applicant"]
title = "Phone number"
required = true

[[question]]
name = ["allows_transactional_text_messages", "applicant"]
title = "Receive texts about your application?"
required = false
alias = "contact_method_sms"
[question.data]
options = [
  { value = "agree_to_transactional_text_messages", label = "I agree to accept text messages about this housing application. You are not required to agree to receive text messages to complete your application." },
]

[[question]]
name = ["phone_alternate", "applicant"]
title = "Alternate phone number"
required = false

[[question]]
name = ["date_of_birth", "applicant"]
title = "Date of birth"
required = true

[[question]]
name = ["gender","applicant"]
title = "Gender"
required = false
alias = "household_member_0_gender"
children = [
  ["gender_pronouns", "applicant"],
]
[question.data]
options = [
  { value = "male", label = "Male" },
  { value = "female", label = "Female" },
  { value = "trans", label = "Trans" },
  { value = "nonbinary", label = "Non-Binary" },
  { value = "other", label = "Other" }
]

[[question]]
name = ["gender_pronouns","applicant"]
title = "Gender Pronoun"
required = false
alias = "household_member_0_gender_pronoun"
cond = { type = "includes", values = [ "trans", "nonbinary", "other" ]}
children = [
  ["gender_pronouns_other", "applicant"],
]
[question.data]
options = [
  { value = "she_her", label = "she/her" },
  { value = "he_him", label = "he/him" },
  { value = "they_them", label = "they/them" },
  { value = "other", label = "Other" },
]

[[question]]
name = ["gender_pronouns_other","applicant"]
title = "Other Pronoun"
required = false
cond = { type = "includes", values = ["other"] }

[[question]]
name = ["household_members_status", "household"]
title = "Will other people be living with you?"
required = true
children = [
  ["household_members", "household"],
]
data = { options = [
  { value = "live_alone", label = "I will be living alone" },
  { value = "with_others", label = "Others will be living with me" },
] }

[[question]]
name = ["household_members", "household"]
title = "Add all the household members below"
required = false
cond = { type = "includes", values = ["with_others"] }
data = { label = "Add Household Members", children = [
  ["name_first", "household_member"],
  ["name_last", "household_member"],
  ["applicant_relationship", "household_member"],
] }

[[question]]
name = ["name_first", "household_member"]
title = "First Name"
required = true
alias = "name_first"

[[question]]
name = ["name_last", "household_member"]
title = "Last Name"
required = true
alias = "name_last"

[[question]]
name = ["applicant_relationship", "household_member"]
title = "Applicant Relationship"
required = false
data = { options = [
  { label = "Co-head/Spouse", value = "Co-head/Spouse" },
  { label = "Domestic Partner", value = "Domestic Partner" },
  { label = "Foster Child", value = "Foster Child" },
  { label = "Dependent Child", value = "Dependent Child" },
  { label = "Unborn Child", value = "Unborn Child" },
  { label = "Child (18+)", value = "Child (18+)" },
  { label = "Child to be Adopted", value = "Child to be Adopted" },
  { label = "Expectant Child of House Member", value = "Expectant Child of House Member" },
  { label = "Parent", value = "Parent" },
  { label = "Sibling", value = "Sibling" },
  { label = "Cousin", value = "Cousin" },
  { label = "Aunt", value = "Aunt" },
  { label = "Uncle", value = "Uncle" },
  { label = "Nephew", value = "Nephew" },
  { label = "Niece", value = "Niece" },
  { label = "Grandparent", value = "Grandparent" },
  { label = "Great Grandparent", value = "Great Grandparent" },
  { label = "In-law", value = "In-law" },
  { label = "Friend", value = "Friend" },
  { label = "Live in Caregiver", value = "Live in Caregiver" },
  { label = "Other Family Member", value = "Other Family Member" },
  { label = "Other", value = "Other" },
] }

[[question]]
name = ["address_residence", "applicant"]
title = "Current residential address"
required = true

[[question]]
name = ["mailing_address_type", "applicant"]
title = "Where do you want to receive mail from us?"
required = true
alias = "mailing_address_type"
children = [
  ["address_mailing", "applicant"],
]
[question.data]
layout = { type = "radio", wrap = false }
options = [
  { value = "residence", label = "Residential" },
  { value = "mailing", label = "Mailing" },
]

[[question]]
name = ["address_mailing", "applicant"]
title = "Mailing Address"
required = false
cond = { type = "includes", values = ["mailing"] }

[[question]]
name = ["household_income", "household"]
title = "Total household income"
required = true

[[question]]
name = ["income_frequency", "household"]
title = "Income frequency"
required = true
[question.data]
layout = { type = "radio", wrap = true }
options = [
  { value = "month", label = "Per month" },
  { value = "year", label = "Per year" },
]

#-----Group: REFERRAL INFO -----

[[question]]
name = ["marketing_how_did_applicant_hear_about_property", "applicant"]
title = "How did you hear about this property?"
required = false
alias = "how_did_applicant_hear_about_property"
children = [
  ["marketing_resident_referral", "applicant"],
  ["marketing_resident_referral_property", "applicant"],
  ["marketing_other_source", "applicant"],
]
[question.data]
options = [
  { value = "Newspaper", label = "Newspaper" },
  { value = "Property Website", label = "Property Website" },
  { value = "Drive by", label = "Drive by" },
  { value = "Flyer", label = "Flyer" },
  { value = "Email Alert", label = "Email Alert" },
  { value = "Friend", label = "Friend" },
  { value = "Housing Counselor or Case Manager", label = "Housing Counselor or Case Manager" },
  { value = "Facebook", label = "Facebook" },
  { value = "Google search", label = "Google search" },
  { value = "Haven Connect", label = "Haven Connect" },
  { value = "Resident Referral", label = "Resident Referral" },
  { value = "Other", label = "Other" },
]

[[question]]
name = ["marketing_resident_referral","applicant"]
title = "Resident Name"
required = false
alias = "property_reference_type_applicant_who"
cond = { type = "includes", values = ["Resident Referral"] }

[[question]]
name = ["marketing_resident_referral_property","applicant"]
title = "Resident's property"
required = false
alias = "property_reference_type_applicant_res_property"
cond = { type = "includes", values = ["Resident Referral"] }

[[question]]
name = ["marketing_other_source","applicant"]
title = "Other Source"
required = false
alias = "property_reference_type_applicant_other"
cond = { type = "includes", values = ["Other"] }

[[question]]
name = ["applicant_relationship", "contact"]
title = "Is there someone you'd like us to contact if we can't reach you?"
required = true
alias = "contact_relation"
children = [
  ["name_full", "contact"],
  ["email", "contact"],
  ["header", "contact_email"],
  ["phone", "contact"],
  ["header", "contact"],
]
[question.data]
options = [
  { value = "friend", label = "Friend" },
  { value = "family_member", label = "Family Member" },
  { value = "case_manager", label = "Case Manager or Housing Counselor" },
  { value = "other", label = "Other" },
  { value = "I don't have an alternate contact", label = "I don't have an alternate contact" },
]

[[question]]
name = ["name_full", "contact"]
title = "Name of Additional Contact Person or Organization"
required = true
alias = "contact_name"
cond = { type = "includes", values = ["friend", "family_member", "case_manager", "other"] }

[[question]]
name = ["email", "contact"]
title = "Optional Contact E-mail Address"
required = true
alias = "contact_email"
cond = { type = "includes", values = ["friend", "family_member", "case_manager", "other"] }

[[question]]
name = ["header", "contact_email"]
title = "Is there someone you'd like us to contact if we can't reach you?*"
required = false
cond = { type = "includes", values = ["friend", "family_member", "case_manager", "other"] }
[question.data]
content = """Your optional contact will be emailed a copy of your application receipt so they can help you with the application process. They will also be copied on emails from the property management company. You can remove your optional contact online at any time.
"""

[[question]]
name = ["phone", "contact"]
title = "Optional Contact Phone Number"
required = false
alias = "contact_primary_phone"
cond = { type = "includes", values = ["friend", "family_member", "case_manager", "other"] }

[[question]]
name = ["header", "contact"]
title = "Is there someone you'd like us to contact if we can't reach you?*"
required = false
cond = { type = "includes", values = ["friend", "family_member", "case_manager", "other"] }
[question.data]
content = """
**Confidentiality Statement:** The information provided on this form is confidential and will not be disclosed to
anyone except as permitted by the applicant or applicable law.
"""

[[question]]
name = ["has_section_8_voucher", "household"]
title = "Do you or anyone you plan to have living with you currently have a Rental Voucher from a Housing Authority (such as Section 8)?"
required = true

[[question]]
name = ["household_requires_impairment_mobility", "household"]
title = "Do you or anyone who will be living with you require special unit design features for mobility impairment?"
required = true

[[question]]
name = ["household_requires_impairment_visual", "household"]
title = "Do you or anyone who will be living with you require special unit design features for visual impairment?"
required = true

[[question]]
name = ["household_requires_impairment_hearing", "household"]
title = "Do you or anyone who will be living with you require special unit design features for hearing impairment?"
required = true

[[question]]
name = ["household_receives_other_housing_assistance", "household"]
title = "Do you or anyone you plan to have living with you currently receive any housing assistance program OTHER than Section 8?"
required = true
children = [
  ["household_receives_other_housing_assistance_subsidy_type", "household"],
]

[[question]]
name = ["household_receives_other_housing_assistance_subsidy_type", "household"]
title = "If yes, please describe the program or subsidy type"
required = true
cond = { type = "includes", values = ["true"] }

[[question]]
name = ["student_household", "household"]
title = "Are all household members full-time students?"
required = true

[[question]]
name = ["unit_size", "applicant"]
title = "Preferred unit size"
required = true
data = { options = [
  { label = "Studio", value = "studio" },
  { label = "1 bedroom", value = "1_bedroom" },
  { label = "2 bedrooms", value = "2_bedrooms" },
  { label = "3 bedrooms", value = "3_bedrooms" },
] }

[[question]]
name = ["formatted_agree_to_terms", "applicant"]
title = ""
required = false
[question.data]
content = "I declare that statements contained in this application are true and complete to the best of my/our knowledge. I have not withheld, falsified or otherwise misrepresented any information. I authorize Haven Connect and/or [Organization] to contact any references listed above and to obtain verification of the information disclosed in the Affordable Housing Program Application which may include employment, income, asset and student status verification for you and any occupants in the premises. You and all occupants hereby release from all liability or responsibility all persons and corporations requesting or supplying such information. You acknowledge that false, incomplete or misleading information herein may constitute grounds for rejection of the application, termination of right of occupancy of all residents and occupants under a lease and/or forfeiture of deposits and fees, and may constitute a criminal offense under the laws of this State. I have read and agree to the provisions as stated. "

[[question]]
name = ["agree_to_terms", "applicant"]
title = "Do you agree?"
required = true
alias = "agree_to_org_terms"

[[question]]
name = ["allows_marketing_texts", "applicant"]
title = "Receive texts from Haven Connect?"
required = false
[question.data]
options = [
  { value = "allows_marketing_texts", label = "I agree to accept text messages about offers and special promotions from Haven Connect (such as other affordable housing opportunities) and its business partners. You are not required to agree to receive promotional text messages to complete your application or receive text messages about your housing application services from Haven Connect. You may withdraw your consent at any time."}
]

[[question]]
name = ["formatted_footer_text", "applicant"]
title = "[Organization] is an Equal Housing Opportunity Provider. We do not discriminate on the basis of disability status in the admission or access, or treatment or employment in, our federally assisted programs and activities. The 504 Coordinator has been designed to coordinate compliance with the nondiscrimination requirements contained in the Department of Housing and Urban Development's regulations implementing Section 504 (24 CFR, part 8 dated June 2, 1988). The 504 Coordinator can be reached by mail at [Address], by email at [email], or by calling [phone]."
required = false
alias = "formatted_footer_text"
[question.data]
content = """[Organization] is an Equal Housing Opportunity Provider. We do not discriminate on the basis of disability status in the admission or access, or treatment or employment in, our federally assisted programs and activities. The 504 Coordinator has been designed to coordinate compliance with the nondiscrimination requirements contained in the Department of Housing and Urban Development's regulations implementing Section 504 (24 CFR, part 8 dated June 2, 1988). The 504 Coordinator can be reached by mail at [Address], by email at [email], or by calling [phone].

"""
`;
